/**
 * Post-job flow shell (PRD §5.5 / §7): a thin frame over the machine-driven
 * four-step wizard in ./post-job. All state, validation, and submit decisions
 * live in `use-post-job-flow` + `post-job-machine`; this component renders the
 * progress chrome, the current step, and the primary actions, and preserves
 * the existing origin / back-handler / beforeRemove contracts.
 */
import { useEffect } from 'react';
import {
  ActivityIndicator,
  BackHandler,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useRouter } from 'expo-router';
import { Text } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft, ArrowRight } from 'lucide-react-native';
import { colors, spacing } from '@tabbio/tokens';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';
import { NativeButton } from '../common/NativeButton';
import { PlatformKeyboardAwareScrollView } from '../common/PlatformKeyboardAwareScrollView';
import type { PostJobOrigin } from './post-job-flow.submit';
import {
  POST_JOB_STEP_COUNT,
  type PostJobStep,
} from './post-job/post-job-machine';
import { usePostJobFlow } from './post-job/use-post-job-flow';
import { PostJobEntryStep } from './post-job/PostJobEntryStep';
import { PostJobBasicsStep } from './post-job/PostJobBasicsStep';
import { PostJobRequirementsStep } from './post-job/PostJobRequirementsStep';
import { PostJobApplicationsStep } from './post-job/PostJobApplicationsStep';
import { PostJobReviewStep } from './post-job/PostJobReviewStep';

const WEB_CONTENT_MAX_WIDTH = 680;

interface PostJobFlowProps {
  origin?: PostJobOrigin;
  /** Present in edit mode: prefill this job and save changes instead of creating. */
  jobId?: string | null;
  onBackActionRegister?: (handler: () => boolean) => void;
}

const STEP_TITLE_KEYS: Record<Exclude<PostJobStep, 'entry'>, string> = {
  basics: 'jobFlow.steps.basics',
  requirements: 'jobFlow.steps.requirements',
  applications: 'jobFlow.steps.applications',
  review: 'jobFlow.steps.review',
};

export function PostJobFlow({
  origin = 'jobs',
  jobId = null,
  onBackActionRegister,
}: PostJobFlowProps = {}) {
  const { t } = useTranslation('common');
  const { t: tEmployer } = useTranslation('employer');
  const { theme } = useTheme();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { isRTL } = useLocale();
  const isWeb = Platform.OS === 'web';
  const isNative = !isWeb;
  // Mirror the back arrow by swapping the glyph, never by rotating it.
  // lucide-react-native forwards `style` to every child element, so a
  // `rotate(180deg)` landed on the <svg> AND on each <path>; an SVG child's
  // transform rotates about the viewBox origin (not the element centre), which
  // threw the geometry outside the 0 0 24 24 viewBox where it was clipped —
  // the Arabic wizard header rendered a blank corner with no visible way out.
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  const flow = usePostJobFlow({ origin, jobId });
  const {
    step,
    progressIndex,
    isEditMode,
    isHydrating,
    jobNotFound,
    isGenerating,
    isSubmitting,
    submittingAs,
  } = flow;

  useEffect(() => {
    if (!onBackActionRegister) return;
    onBackActionRegister(() => flow.stepBack());
  }, [flow, flow.stepBack, onBackActionRegister]);

  useEffect(() => {
    if (Platform.OS !== 'android') return;
    const subscription = BackHandler.addEventListener('hardwareBackPress', () =>
      flow.stepBack(),
    );
    return () => subscription.remove();
  }, [flow]);

  // The routes present this flow headerless on every platform (iOS formSheet,
  // Android card, web dialog), so the wizard always draws its own top bar.
  // On iOS the sheet starts below the status bar yet `insets.top` still
  // reports the window's notch inset, so honouring it padded a whole status
  // bar of dead space above the back arrow: use a fixed sheet inset there.
  // Android's edge-to-edge card and web still need the real safe area.
  const headerTopInset =
    Platform.OS === 'ios' ? spacing[3] : Math.max(insets.top, spacing[3]);

  // Header back: step back in-flow first; once at the flow root the route's
  // beforeRemove guard owns actual dismissal of the screen (web dialog /
  // native sheet).
  const handleHeaderBack = () => {
    if (flow.stepBack()) return;
    if (isWeb) {
      if (typeof window !== 'undefined') window.history.back();
      return;
    }
    router.back();
  };

  // Interstitial states (generating / hydrating / not-found) keep a bare
  // back-arrow row so the headerless sheet/dialog is never a dead end.
  const backOnlyHeader = (
    <View style={[styles.headerPlain, { paddingTop: headerTopInset }]}>
      <View style={styles.headerRow}>
        <Pressable
          onPress={handleHeaderBack}
          style={styles.headerButton}
          hitSlop={8}
        >
          <BackIcon size={20} color={theme.text} strokeWidth={2} />
        </Pressable>
        <View style={styles.headerButton} />
        <View style={styles.headerButton} />
      </View>
    </View>
  );

  if (isGenerating) {
    return (
      <View style={[styles.screen, { backgroundColor: theme.background }]}>
        {backOnlyHeader}
        <View style={[styles.screen, styles.centered]}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.generatingTitle, { color: theme.text }]}>
            {t('jobPost.generating.title')}
          </Text>
          <Text
            style={[styles.generatingSubtitle, { color: theme.textSecondary }]}
          >
            {t('jobPost.generating.subtitle')}
          </Text>
          <View style={styles.skeletonWrap}>
            {[0, 1, 2, 3, 4].map((index) => (
              <View
                key={index}
                style={[
                  styles.skeletonLine,
                  {
                    width: [190, 150, 220, 170, 200][index],
                    backgroundColor: theme.borderSubtle,
                  },
                ]}
              />
            ))}
          </View>
        </View>
      </View>
    );
  }

  if (isHydrating) {
    return (
      <View style={[styles.screen, { backgroundColor: theme.background }]}>
        {backOnlyHeader}
        <View style={[styles.screen, styles.centered]}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text
            style={[styles.generatingSubtitle, { color: theme.textSecondary }]}
          >
            {tEmployer('jobFlow.edit.loading')}
          </Text>
        </View>
      </View>
    );
  }

  if (jobNotFound) {
    return (
      <View style={[styles.screen, { backgroundColor: theme.background }]}>
        {backOnlyHeader}
        <View style={[styles.screen, styles.centered]}>
          <Text style={[styles.generatingTitle, { color: theme.text }]}>
            {tEmployer('jobFlow.edit.notFoundTitle')}
          </Text>
          <Text
            style={[styles.generatingSubtitle, { color: theme.textSecondary }]}
          >
            {tEmployer('jobFlow.edit.notFoundBody')}
          </Text>
        </View>
      </View>
    );
  }

  const onEntry = step === 'entry';
  const currentTitle = onEntry
    ? t('jobPost.step1.title')
    : (tEmployer(`${STEP_TITLE_KEYS[step]}.title` as never) as string);
  const currentSubtitle = onEntry
    ? t('jobPost.step1.subtitle')
    : (tEmployer(`${STEP_TITLE_KEYS[step]}.subtitle` as never) as string);

  const progress =
    progressIndex >= 0 ? ((progressIndex + 1) / POST_JOB_STEP_COUNT) * 100 : 0;

  const webHeaderActionDisabled =
    isSubmitting || (onEntry && !flow.state.entry.mode);

  const submitLabel = isEditMode
    ? submittingAs !== null
      ? (tEmployer('jobFlow.actions.saving') as string)
      : (tEmployer('jobFlow.actions.saveChanges') as string)
    : submittingAs === 'live'
      ? (tEmployer('jobFlow.actions.publishing') as string)
      : (tEmployer('jobFlow.actions.publish') as string);

  const reviewButtons = (
    <View style={styles.reviewButtons}>
      {!isEditMode ? (
        <View style={styles.flexOne}>
          <NativeButton
            title={
              submittingAs === 'draft'
                ? (tEmployer('jobFlow.actions.saving') as string)
                : (tEmployer('jobFlow.actions.saveDraft') as string)
            }
            onPress={() => void flow.submit('draft')}
            variant="outline"
            disabled={isSubmitting}
            loading={submittingAs === 'draft'}
            size="lg"
            pill
            block
          />
        </View>
      ) : null}
      <View style={styles.flexOne}>
        <NativeButton
          title={submitLabel}
          onPress={() => void flow.submit('live')}
          disabled={isSubmitting}
          loading={submittingAs === 'live' || submittingAs === 'update'}
          size="lg"
          pill
          block
        />
      </View>
    </View>
  );

  // Edit mode keeps "Save changes" reachable from every step: the wizard is a
  // four-step CREATE flow, but an employer editing a live role usually wants
  // to change one field and leave — walking to step 4 to find the only Save
  // was the reason a title-only edit felt impossible.
  const showBottomBar = isNative || step === 'review' || isEditMode;

  return (
    <KeyboardAvoidingView
      style={[styles.screen, { backgroundColor: theme.background }]}
      behavior="padding"
      automaticOffset
    >
      <View
        style={[
          styles.header,
          {
            borderBottomColor: theme.borderSubtle,
            paddingTop: headerTopInset,
          },
        ]}
      >
        <View style={styles.headerRow}>
          <Pressable
            onPress={handleHeaderBack}
            style={styles.headerButton}
            hitSlop={8}
          >
            <BackIcon size={20} color={theme.text} strokeWidth={2} />
          </Pressable>
          <Text style={[styles.headerTitle, { color: theme.text }]}>
            {progressIndex >= 0
              ? t('jobPost.stepCounter', {
                  current: progressIndex + 1,
                  total: POST_JOB_STEP_COUNT,
                })
              : ''}
          </Text>
          {/* Web advances from the header; native keeps the bottom-bar CTA. */}
          {isWeb && step !== 'review' ? (
            <Pressable
              onPress={() => flow.goNext()}
              disabled={webHeaderActionDisabled}
              style={styles.headerButton}
              hitSlop={8}
            >
              <Text
                style={[
                  styles.headerAction,
                  {
                    color: webHeaderActionDisabled
                      ? theme.textTertiary
                      : colors.primary,
                  },
                ]}
              >
                {t('jobPost.actions.next')}
              </Text>
            </Pressable>
          ) : (
            <View style={styles.headerButton} />
          )}
        </View>
        <View
          style={[
            styles.progressTrack,
            { backgroundColor: theme.borderSubtle },
          ]}
        >
          <View
            style={[
              styles.progressFill,
              { width: `${progress}%`, backgroundColor: colors.primary },
            ]}
          />
        </View>
      </View>

      <PlatformKeyboardAwareScrollView
        style={styles.scroll}
        contentInsetAdjustmentBehavior={isNative ? 'never' : undefined}
        contentContainerStyle={[
          styles.scrollContent,
          {
            // Web sits in a content-hugging dialog: the inner block's own
            // bottom padding is the whole gap, anything more reads as a dead
            // half-card under the short entry step.
            paddingBottom: isWeb ? 0 : insets.bottom + spacing[12],
          },
        ]}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode={isNative ? 'interactive' : 'on-drag'}
        bottomOffset={isNative ? 128 : 24}
      >
        <View style={[styles.contentInner, isWeb && styles.contentInnerWeb]}>
          {/* Progress lives in the top bar (counter + track) on all platforms
              now that the wizard owns its header everywhere. */}
          <View style={styles.titleBlock}>
            <Text
              style={[
                styles.title,
                isWeb && styles.titleWeb,
                { color: theme.text },
              ]}
            >
              {currentTitle}
            </Text>
            <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
              {currentSubtitle}
            </Text>
          </View>

          {step === 'entry' ? <PostJobEntryStep flow={flow} /> : null}
          {step === 'basics' ? <PostJobBasicsStep flow={flow} /> : null}
          {step === 'requirements' ? (
            <PostJobRequirementsStep flow={flow} />
          ) : null}
          {step === 'applications' ? (
            <PostJobApplicationsStep flow={flow} />
          ) : null}
          {step === 'review' ? <PostJobReviewStep flow={flow} /> : null}
        </View>
      </PlatformKeyboardAwareScrollView>

      {showBottomBar ? (
        <View
          style={[
            styles.bottomBar,
            {
              borderTopColor: theme.borderSubtle,
              backgroundColor: theme.background,
              paddingBottom: Math.max(insets.bottom, spacing[3]),
            },
          ]}
        >
          <View
            style={[styles.bottomBarInner, isWeb && styles.bottomBarInnerWeb]}
          >
            {step === 'review' ? (
              reviewButtons
            ) : onEntry ? (
              <NativeButton
                title={t('jobPost.actions.postManually')}
                onPress={() => flow.startManually()}
                size="lg"
                pill
                block
              />
            ) : isEditMode ? (
              <View style={styles.reviewButtons}>
                <View style={styles.flexOne}>
                  <NativeButton
                    title={submitLabel}
                    onPress={() => void flow.submit('live')}
                    variant="outline"
                    disabled={isSubmitting}
                    loading={submittingAs === 'update'}
                    size="lg"
                    pill
                    block
                  />
                </View>
                <View style={styles.flexOne}>
                  <NativeButton
                    title={t('jobPost.actions.next')}
                    onPress={() => flow.goNext()}
                    disabled={isSubmitting}
                    size="lg"
                    pill
                    block
                  />
                </View>
              </View>
            ) : (
              <NativeButton
                title={t('jobPost.actions.next')}
                onPress={() => flow.goNext()}
                size="lg"
                pill
                block
              />
            )}
          </View>
        </View>
      ) : null}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing[6],
    gap: spacing[2],
  },
  generatingTitle: {
    marginTop: spacing[4],
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
  },
  generatingSubtitle: {
    marginTop: spacing[1],
    fontSize: 14,
    textAlign: 'center',
  },
  skeletonWrap: {
    marginTop: spacing[6],
    gap: spacing[2],
  },
  skeletonLine: {
    height: 10,
    borderRadius: 999,
  },
  header: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: spacing[4],
    paddingBottom: spacing[3],
  },
  // Interstitial variant: same geometry, no rule under it.
  headerPlain: {
    paddingHorizontal: spacing[4],
    paddingBottom: spacing[3],
  },
  headerRow: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerButton: {
    minWidth: 56,
    minHeight: 38,
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: '700',
  },
  headerAction: {
    fontSize: 15,
    fontWeight: '600',
  },
  progressTrack: {
    width: '100%',
    height: 2,
    borderRadius: 999,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  contentInner: {
    paddingHorizontal: spacing[4],
    paddingTop: spacing[6],
    paddingBottom: spacing[8],
  },
  contentInnerWeb: {
    width: '100%',
    maxWidth: WEB_CONTENT_MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: spacing[6],
    paddingTop: spacing[6],
  },
  titleBlock: {
    marginBottom: spacing[8],
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    letterSpacing: -0.2,
  },
  titleWeb: {
    fontSize: 28,
  },
  subtitle: {
    marginTop: spacing[2],
    fontSize: 14,
    lineHeight: 21,
  },
  bottomBar: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: spacing[4],
    paddingTop: spacing[4],
  },
  bottomBarInner: {
    width: '100%',
  },
  bottomBarInnerWeb: {
    maxWidth: WEB_CONTENT_MAX_WIDTH,
    alignSelf: 'center',
  },
  reviewButtons: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  flexOne: {
    flex: 1,
  },
});
